import React, { useState } from 'react';
import { useQuery } from 'react-query';
import { Link } from 'react-router-dom';
import { 
  BuildingStorefrontIcon, 
  MagnifyingGlassIcon,
  MapPinIcon
} from '@heroicons/react/24/outline';
import api from '../services/api';
import LoadingSpinner from '../components/ui/LoadingSpinner';
import StarRating from '../components/ui/StarRating';
import ReviewModal from '../components/reviews/ReviewModal';
import ReviewForm from '../components/reviews/ReviewForm';
import { useAuth } from '../context/AuthContext';

const Vendors = () => {
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedVendor, setSelectedVendor] = useState(null);
  const [showReviewModal, setShowReviewModal] = useState(false);
  const [showReviewForm, setShowReviewForm] = useState(false);
  const { user } = useAuth();

  const { data: vendorsData, isLoading } = useQuery(
    'vendors',
    () => api.get('/vendors').then(res => res.data.data)
  );

  const handleViewReviews = (vendor, e) => {
    e.preventDefault();
    e.stopPropagation();
    setSelectedVendor(vendor);
    setShowReviewModal(true);
  };

  const handleAddReview = (vendor, e) => {
    e.preventDefault();
    e.stopPropagation();
    setSelectedVendor(vendor);
    setShowReviewForm(true);
  };

  // API returns { vendors: [...], pagination: {...} }
  const vendors = vendorsData?.vendors || [];

  // Debug: log the data
  console.log('Vendors Data:', vendorsData);
  console.log('Vendors Array:', vendors);

  // Filter vendors based on search
  const filteredVendors = vendors.filter(vendor => 
    vendor.store_name?.toLowerCase().includes(searchQuery.toLowerCase()) ||
    vendor.description?.toLowerCase().includes(searchQuery.toLowerCase())
  );

  if (isLoading) {
    return (
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <div className="flex justify-center items-center h-64">
          <LoadingSpinner />
        </div>
      </div>
    );
  }

  return (
    <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
      {/* Header */}
      <div className="mb-8">
        <h1 className="text-3xl font-bold text-gray-900 mb-4">المتاجر</h1>
        <p className="text-gray-600">تصفح جميع المتاجر المتاحة على المنصة</p>
      </div>

      {/* Search */}
      <div className="mb-6">
        <div className="relative max-w-md">
          <input
            type="text"
            placeholder="ابحث عن متجر..."
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-500"
          />
          <MagnifyingGlassIcon className="absolute left-3 top-1/2 transform -translate-y-1/2 w-5 h-5 text-gray-400" />
        </div>
      </div>

      {/* Vendors Grid */}
      {filteredVendors.length > 0 ? (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          {filteredVendors.map((vendor) => (
            <Link
              key={vendor.id}
              to={`/vendors/store/${vendor.store_slug}`}
              className="bg-white rounded-lg shadow hover:shadow-lg transition-shadow overflow-hidden"
            >
              {/* Store Banner/Background */}
              <div className="relative h-32 bg-gradient-to-br from-primary-500 to-primary-700">
                {vendor.banner_url ? (
                  <img
                    src={vendor.banner_url}
                    alt={vendor.store_name}
                    className="w-full h-full object-cover"
                  />
                ) : (
                  <div className="w-full h-full bg-gradient-to-br from-primary-500 to-primary-700" />
                )}
                
                {/* Store Logo - Circular and overlapping */}
                <div className="absolute -bottom-12 right-1/2 transform translate-x-1/2">
                  {vendor.logo_url ? (
                    <img
                      src={vendor.logo_url}
                      alt={vendor.store_name}
                      className="w-24 h-24 rounded-full object-cover border-4 border-white shadow-lg"
                    />
                  ) : (
                    <div className="w-24 h-24 rounded-full bg-white border-4 border-white shadow-lg flex items-center justify-center">
                      <BuildingStorefrontIcon className="w-12 h-12 text-primary-600" />
                    </div>
                  )}
                </div>
              </div>

              {/* Store Info */}
              <div className="p-6 pt-14">
                <h3 className="text-xl font-bold text-gray-900 mb-2 text-center">
                  {vendor.store_name}
                </h3>
                
                {vendor.description && (
                  <p className="text-gray-600 text-sm mb-4 line-clamp-2 text-center">
                    {vendor.description}
                  </p>
                )}

                {/* Stats */}
                <div className="flex items-center justify-center gap-4 text-sm text-gray-500 mb-4">
                  {/* Rating with click to view reviews */}
                  {vendor.rating_average > 0 && (
                    <button
                      onClick={(e) => handleViewReviews(vendor, e)}
                      className="flex items-center gap-1 hover:text-primary-600 transition-colors"
                    >
                      <StarRating 
                        rating={Math.round(vendor.rating_average)} 
                        size="sm" 
                      />
                      <span className="font-medium">
                        {parseFloat(vendor.rating_average).toFixed(1)}
                      </span>
                      {vendor.rating_count > 0 && (
                        <span className="text-gray-400">
                          ({vendor.rating_count})
                        </span>
                      )}
                    </button>
                  )}
                  {vendor.products_count !== undefined && (
                    <span>{vendor.products_count} منتج</span>
                  )}
                </div>

                {/* Location */}
                {vendor.address && (
                  <div className="flex items-center justify-center gap-2 text-sm text-gray-500 mb-4">
                    <MapPinIcon className="w-4 h-4" />
                    <span className="line-clamp-1">{vendor.address}</span>
                  </div>
                )}

                {/* View Store Button */}
                <div className="pt-4 border-t border-gray-200 text-center">
                  <span className="text-primary-600 font-medium hover:text-primary-700">
                    زيارة المتجر ←
                  </span>
                </div>

                {/* Add Review Button - Only for logged in customers */}
                {user && user.role === 'customer' && (
                  <div className="pt-3">
                    <button
                      onClick={(e) => handleAddReview(vendor, e)}
                      className="w-full py-2 text-sm text-primary-600 border border-primary-600 rounded-lg hover:bg-primary-50 transition-colors"
                    >
                      إضافة تقييم
                    </button>
                  </div>
                )}
              </div>
            </Link>
          ))}
        </div>
      ) : (
        <div className="bg-white rounded-lg shadow p-12 text-center">
          <BuildingStorefrontIcon className="w-16 h-16 mx-auto text-gray-400 mb-4" />
          <h2 className="text-2xl font-bold text-gray-900 mb-2">
            {searchQuery ? 'لا توجد نتائج' : 'لا توجد متاجر'}
          </h2>
          <p className="text-gray-600">
            {searchQuery 
              ? 'جرب البحث بكلمات مختلفة'
              : 'لا توجد متاجر متاحة حالياً'
            }
          </p>
        </div>
      )}

      {/* Review Modal */}
      {selectedVendor && (
        <>
          <ReviewModal
            isOpen={showReviewModal}
            onClose={() => {
              setShowReviewModal(false);
              setSelectedVendor(null);
            }}
            vendorId={selectedVendor.id}
            vendorName={selectedVendor.store_name}
          />
          
          <ReviewForm
            isOpen={showReviewForm}
            onClose={() => {
              setShowReviewForm(false);
              setSelectedVendor(null);
            }}
            vendorId={selectedVendor.id}
            vendorName={selectedVendor.store_name}
            type="vendor"
          />
        </>
      )}
    </div>
  );
};

export default Vendors;
